import React, { useState } from "react";
import { Descriptions, Tag, Typography, Button, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { EndpointMessageInterface } from "../../../interfaces/EndpointMessage.interface";
import { formatForClipboard } from "./endpointMessageUtils";

const { Text } = Typography;

function formatJson(data: unknown): string {
  if (data === null || data === undefined) return "null";
  try {
    return JSON.stringify(
      data,
      (_key, value) => (typeof value === "bigint" ? value.toString() : value),
      2,
    );
  } catch {
    return String(data);
  }
}

export default function TerminalEndpointDetail({ message }: { message: EndpointMessageInterface }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(formatForClipboard(message)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div style={{ padding: "8px 16px", background: "#1a1a2e" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Tooltip title={copied ? "Copied!" : "Copy all"}>
          <Button
            size="small"
            type="default"
            icon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
            onClick={handleCopy}
            style={{ color: copied ? "#52c41a" : undefined, fontSize: 11 }}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
        </Tooltip>
      </div>
      <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Method">
          <Tag color={message.method === "GET" ? "blue" : "green"}>{message.method}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="URL" span={2}>
          <Text code style={{ fontSize: 11 }}>
            {message.url}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={message.status < 400 ? "green" : "red"}>{message.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Duration">{message.duration}ms</Descriptions.Item>
        <Descriptions.Item label="Format">
          <Tag color={message.isFlatBuffer ? "purple" : "default"}>
            {message.isFlatBuffer ? "FlatBuffer" : "JSON"}
          </Tag>
        </Descriptions.Item>
        {message.typeName && (
          <Descriptions.Item label="Type Name" span={2}>
            <Text code>{message.typeName}</Text>
          </Descriptions.Item>
        )}
        {message.schemaHash && (
          <Descriptions.Item label="Schema Hash">
            <Text code style={{ fontSize: 10 }}>
              {message.schemaHash.slice(0, 16)}…
            </Text>
          </Descriptions.Item>
        )}
        {message.error && (
          <Descriptions.Item label="Error" span={3}>
            <Tag color="red">{message.error}</Tag>
          </Descriptions.Item>
        )}
      </Descriptions>

      <div style={{ display: "flex", gap: 12 }}>
        {message.requestBody && (
          <div style={{ flex: 1 }}>
            <Text strong style={{ color: "#888", fontSize: 11 }}>
              REQUEST BODY
            </Text>
            <pre
              style={{
                background: "#0d1117",
                color: "#c9d1d9",
                padding: 8,
                borderRadius: 4,
                fontSize: 11,
                maxHeight: 300,
                overflow: "auto",
                marginTop: 4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {formatJson(message.requestBody)}
            </pre>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <Text strong style={{ color: "#888", fontSize: 11 }}>
            RESPONSE BODY
          </Text>
          <pre
            style={{
              background: "#0d1117",
              color: "#c9d1d9",
              padding: 8,
              borderRadius: 4,
              fontSize: 11,
              maxHeight: 300,
              overflow: "auto",
              marginTop: 4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {formatJson(message.responseBody)}
          </pre>
        </div>
      </div>
    </div>
  );
}
